/**
 * Pedido de redefinição de senha.
 *
 * A tela **sempre** confirma, mesmo quando o e-mail não existe. Não é
 * descuido: responder "esse e-mail não está cadastrado" transformaria este
 * formulário num verificador de contas — qualquer pessoa poderia descobrir
 * quem tem acesso à plataforma. O backend segue a mesma regra e devolve 204 em
 * todos os casos.
 *
 * Por isso o texto de confirmação fala em "se houver uma conta": é honesto com
 * quem digitou errado, sem entregar nada a quem está sondando.
 */

import { useState } from "react";
import { Logo } from "../ds";
import { Link } from "react-router-dom";

import { ApiError, apiFetch } from "../api/client";

export default function EsqueciSenhaPage() {
  const [email, setEmail] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    setEnviando(true);
    setErro(null);

    try {
      await apiFetch("/auth/password/forgot", {
        method: "POST",
        body: { email: email.trim() },
      });
      setEnviado(true);
    } catch (falha) {
      // Só falha de rede ou limite de taxa chega aqui — o servidor não
      // distingue e-mail conhecido de desconhecido.
      setErro(
        falha instanceof ApiError
          ? falha.message
          : "Não foi possível enviar agora. Verifique sua conexão e tente de novo.",
      );
    } finally {
      setEnviando(false);
    }
  }

  if (enviado) {
    return (
      <div className="shell">
        <div className="brand">
          <Logo tamanho={32} />
          <b>Aletia</b>
          <span>Armaturen · Valve Passport</span>
        </div>

        <div className="card card-estreito">
          <h1>Verifique seu e-mail</h1>
          <p className="sub">
            Se houver uma conta com <b>{email.trim()}</b>, enviamos um link para escolher uma nova
            senha. Ele vale por tempo limitado e só pode ser usado uma vez.
          </p>
          <p className="muted">
            Não chegou? Confira a caixa de spam. O envio pode levar alguns minutos.
          </p>
          <div className="acoes">
            <Link className="btn btn-primario" to="/entrar">
              Voltar para o login
            </Link>
            <button type="button" className="btn" onClick={() => setEnviado(false)}>
              Usar outro e-mail
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shell">
      <div className="brand">
        <Logo tamanho={32} />
        <b>Aletia</b>
        <span>Armaturen · Valve Passport</span>
      </div>

      <form className="card card-estreito" onSubmit={enviar}>
        <h1>Esqueci minha senha</h1>
        <p className="sub">
          Informe o e-mail da sua conta. Enviaremos um link para você escolher uma nova senha.
        </p>

        <label className="campo">
          <span>E-mail</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            autoFocus
          />
        </label>

        {erro && (
          <p className="alerta" role="alert">
            {erro}
          </p>
        )}

        <button type="submit" className="btn btn-primario" disabled={enviando}>
          {enviando ? "Enviando…" : "Enviar link de redefinição"}
        </button>

        <p className="muted">
          Lembrou a senha? <Link to="/entrar">Voltar para o login</Link>
        </p>
      </form>
    </div>
  );
}
